import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

/** "Expired" UI window after expiry date. */
const EXPIRED_WINDOW_DAYS_AFTER_EXPIRY = 11;
/** "Expiring soon" window before expiry date. */
const EXPIRING_SOON_WINDOW_DAYS_BEFORE_EXPIRY = 30;

/**
 * Parse insurance expiration date (YYYY-MM-DD or dd/MM/yyyy) to start-of-day.
 */
function parseExpiry(dateStr: string): dayjs.Dayjs {
  const trimmed = dateStr.trim();
  return /^\d{4}-\d{2}-\d{2}/.test(trimmed)
    ? dayjs(trimmed).startOf('day')
    : dayjs(trimmed, 'DD/MM/YYYY').startOf('day');
}

export type InsuranceRenewWindow = 'none' | 'expiring_soon' | 'expired';

/**
 * Single source of truth for insurance renew UI state.
 *
 * - expiring_soon: expiry is today or within next 30 days
 * - expired: today is within 1..45 days after expiry
 * - none: otherwise
 */
export function hasInsuranceExpired(
  insuranceExpirationDate: string | undefined | null,
) {
  if (!insuranceExpirationDate) {
    return false;
  }
  const expiry = parseExpiry(insuranceExpirationDate);
  const today = dayjs().startOf('day');
  if (!expiry.isValid()) {
    return false;
  }

  const daysSinceExpiry = today.diff(expiry, 'day');
  
  if (daysSinceExpiry >= 1 && daysSinceExpiry <= EXPIRED_WINDOW_DAYS_AFTER_EXPIRY) {
    return true
  }

  return false
}

export function hasInsuranceExpiringSoon(insuranceExpirationDate: string | undefined | null): boolean { 
  if (!insuranceExpirationDate) {
    return false;
  }
  const expiry = parseExpiry(insuranceExpirationDate);
  const today = dayjs().startOf('day');
  if (!expiry.isValid()) {
    return false;
  }
  const daysUntilExpiry = expiry.diff(today, 'day');
  if (daysUntilExpiry >= 0 && daysUntilExpiry <= EXPIRING_SOON_WINDOW_DAYS_BEFORE_EXPIRY) {
    return true
  }
  return false;
}

export function hasInsuranceExpiringSoonOrExpired(insuranceExpirationDate: string | undefined | null): boolean {
  return hasInsuranceExpired(insuranceExpirationDate) || hasInsuranceExpiringSoon(insuranceExpirationDate);
}

/**
 * True when account has no usable insurance expiration date (legacy accounts).
 */
export function hasInsuranceInfoMissing(
  insuranceExpirationDate: string | undefined | null,
): boolean {
  if (!insuranceExpirationDate?.trim()) {
    return true;
  }
  const expiry = parseExpiry(insuranceExpirationDate);
  return !expiry.isValid();
}

/**
 * Whole days from today (start of day) until expiry, only when in the 30-day reminder window (0–30 inclusive).
 * Matches {@link hasInsuranceExpiringSoon} — use for countdown copy on profile / renew UI.
 */
export function getInsuranceDaysUntilExpiry(
  insuranceExpirationDate: string | undefined | null,
): number | null {
  if (!insuranceExpirationDate) {
    return null;
  }
  const expiry = parseExpiry(insuranceExpirationDate);
  const today = dayjs().startOf('day');
  if (!expiry.isValid()) {
    return null;
  }
  const daysUntilExpiry = expiry.diff(today, 'day');
  if (daysUntilExpiry < 0 || daysUntilExpiry > EXPIRING_SOON_WINDOW_DAYS_BEFORE_EXPIRY) {
    return null;
  }
  return daysUntilExpiry;
}

/**
 * When insurance is expired, returns how many days remain before the Pro status is revoked.
 * Uses the same EXPIRED_WINDOW_DAYS_AFTER_EXPIRY constant (e.g. 11) so when expiry is 1 day ago
 * this returns 10 (i.e. expiry + 11 days total window → 10 days remaining).
 */
export function getInsuranceDaysUntilRevocation(
  insuranceExpirationDate: string | undefined | null,
): number | null {
  if (!insuranceExpirationDate) return null;
  const expiry = parseExpiry(insuranceExpirationDate);
  const today = dayjs().startOf('day');
  if (!expiry.isValid()) return null;
  const daysSinceExpiry = today.diff(expiry, 'day');
  if (daysSinceExpiry < 1 || daysSinceExpiry > EXPIRED_WINDOW_DAYS_AFTER_EXPIRY) {
    return null;
  }
  return EXPIRED_WINDOW_DAYS_AFTER_EXPIRY - daysSinceExpiry;
}

/** Renew-by date for UI: expiry + 45 days, formatted DD/MM/YYYY. */
export function getRenewByDateFormatted(insuranceExpirationDate: string | undefined | null): string | null {
  if (!insuranceExpirationDate) return null;
  const expiry = parseExpiry(insuranceExpirationDate);
  if (!expiry.isValid()) return null;
  return expiry.add(11, 'day').format('DD/MM/YYYY');
}

/**
 * Returns true if insuranceExpirationDate is today or in the future (still valid).
 */
export function hasValidInsurance(
  insuranceExpirationDate: string | undefined | null,
): boolean {
  if (!insuranceExpirationDate) {
    return false;
  }
  const expiry = parseExpiry(insuranceExpirationDate);
  const today = dayjs().startOf('day');
  if (!expiry.isValid()) {
    return false;
  }
  return expiry.diff(today, 'day') >= 0;
}

